import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { pedometerAvailable, startRawTracking, getRawCumulative, setNotifTemplate, setTodaySteps, getDayBaseline } from "../lib/native/pedometer";
import { showRewardAd } from "../lib/native/rewardAd";

// UI 갱신 주기(포인트 정책 아님). 표시(빠름)/서버 동기화(느림) 분리.
export const STEP_DISPLAY_REFRESH_MS = 1500;
export const STEP_SERVER_SYNC_MS = 25000;

// ── lastKnown: "직전에 본 today 게이지 상태"를 즉시 표시용 placeholder로 보관 ──
// 메모리+localStorage. 항상 실제 /steps/today 결과로 덮어써 최신화(돈 정합성).
const LS_LASTKNOWN = "zm_steps_lastknown_v1";
const kstDateStr = () => new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);

// 첫 사용(저장 없음) 시 0 기준 빈 게이지. config는 보수적 기본값(즉시 실제값으로 갱신됨).
const ZERO_TODAY = {
  enabled: true, steps: 0, goal_steps: 10000, daily_cap: 0,
  today_earned_points: 0, claimed_count: 0, daily_claim_limit: 0,
  steps_per_claim: 1000, points_per_claim: 0, baseline: null,
};

function readLastKnownToday() {
  try {
    const raw = localStorage.getItem(LS_LASTKNOWN);
    if (!raw) return { ...ZERO_TODAY, _placeholder: true, _zero: true };
    const saved = JSON.parse(raw);
    if (!saved || !saved.today) return { ...ZERO_TODAY, _placeholder: true, _zero: true };
    if (saved.date_kst === kstDateStr()) {
      // 같은 KST 날짜 → 마지막으로 본 채움 그대로 즉시 표시
      return { ...saved.today, _placeholder: true };
    }
    // 이전 날짜 → 어제 채움을 '오늘'처럼 보이지 않게: config만 유지하고 동적 수치 리셋
    return {
      ...saved.today, steps: 0, claimed_count: 0, today_earned_points: 0,
      baseline: null, pending_bonus_count: 0, _placeholder: true, _reset: true,
    };
  } catch (e) {
    return { ...ZERO_TODAY, _placeholder: true, _zero: true };
  }
}

function persistLastKnownToday(t) {
  if (!t || t._placeholder || t.enabled === false) return;
  try { localStorage.setItem(LS_LASTKNOWN, JSON.stringify({ date_kst: kstDateStr(), today: t })); } catch (e) { /* noop */ }
}

// ── 홈 게이지 표시값 전역 공유(단일 소스) ──
// 홈/걸음 화면(useSteps realtime)이 "지금 화면에 표시 중인 오늘 걸음"을 발행(publish)하고,
// 랭킹 등 다른 화면은 useLiveTodaySteps()로 동일 값을 구독만 한다(재계산·재조회·폴링·센서 접근 없음).
// 모듈 스코프라 라우트 전환(홈 언마운트) 후에도 마지막 표시값이 유지됨. KST 날짜가 다르면 무효(null).
let liveTodayStore = null; // { date_kst, steps }
const liveTodayListeners = new Set();
function publishLiveTodaySteps(steps) {
  const n = Math.max(0, Math.round(Number(steps) || 0));
  if (n <= 0) return;
  const prev = liveTodayStore;
  const today = kstDateStr();
  const sameDay = prev && prev.date_kst === today;
  const next = { date_kst: today, steps: sameDay ? Math.max(prev.steps, n) : n }; // 그날 최댓값 유지(단조성)
  if (sameDay && prev.steps === next.steps) return;
  liveTodayStore = next;
  liveTodayListeners.forEach((l) => { try { l(); } catch (e) { /* noop */ } });
}
const subscribeLiveToday = (l) => { liveTodayListeners.add(l); return () => liveTodayListeners.delete(l); };
const getLiveTodaySnapshot = () => (liveTodayStore && liveTodayStore.date_kst === kstDateStr() ? liveTodayStore.steps : null);
// 홈 게이지가 표시 중인 오늘 걸음(없으면 null). 발행될 때만 리렌더(React 표준 외부 스토어 구독).
export function useLiveTodaySteps() {
  return useSyncExternalStore(subscribeLiveToday, getLiveTodaySnapshot, () => null);
}

// POST /steps/daily payload — 기기 로컬 자정 baseline이 있으면 함께 전달(백엔드 오늘 첫 기록 baseline 교정용).
// getDayBaseline 실패/지연(1.5s)이 동기화를 막지 않게 안전 처리. 없으면 { cumulative }만(기존 동작·회귀 0).
async function buildDailyPayload(cum) {
  try {
    const TIMEOUT = Symbol("timeout");
    const b = await Promise.race([
      getDayBaseline(),
      new Promise((resolve) => setTimeout(() => resolve(TIMEOUT), 1500)),
    ]);
    if (b && b !== TIMEOUT && b.baseline != null) {
      return { cumulative: cum, day_baseline: b.baseline, day_baseline_date: b.baselineDate };
    }
  } catch (e) { /* noop */ }
  return { cumulative: cum };
}

// 랭킹 등 "센서 최신 누적값을 서버에 즉시 1회 반영한 뒤 조회"가 필요한 화면용 일회성 헬퍼.
// - 네이티브(센서)만 동작. 웹/권한없음/hasValue=false면 조용히 false 반환(호출부는 그대로 조회 진행).
// - 짧은 타임아웃으로 감싸 지연/실패가 화면 조회를 막지 않게 한다.
// - 실시간 폴링(useSteps 내부 effect)·플러그인 계약({steps,available,hasValue})·computeDisplay와 무관(독립 1회성).
export async function syncRawStepsOnce(timeoutMs = 4000) {
  if (!pedometerAvailable()) return false;
  const TIMEOUT = Symbol("timeout");
  const withTimeout = (p) => Promise.race([
    Promise.resolve(p),
    new Promise((resolve) => setTimeout(() => resolve(TIMEOUT), timeoutMs)),
  ]);
  try {
    const c = await withTimeout(getRawCumulative());
    if (c === TIMEOUT || c == null || c <= 0) return false; // 센서값 없음/지연 → 건너뜀
    const r = await withTimeout(api.post("/steps/daily", await buildDailyPayload(c)));
    return r !== TIMEOUT;
  } catch (e) {
    return false; // 네트워크 오류 등 → 조용히 무시(랭킹은 서버/캐시값으로 표시)
  }
}

// 만보기 공용 훅 — 홈 게이지 위젯과 /steps 상세가 동일한 적립 흐름을 공유(DRY).
// 네이티브는 raw 누적값을 백엔드로 전송, 백엔드가 KST baseline 차감(권위). 표시는 baseline로 즉시 계산.
export function useSteps({ realtime = false } = {}) {
  const { refresh } = useAuth();
  // 직전 본 상태로 즉시 렌더(빈 화면 멈춤 제거). load()가 실제값으로 즉시 덮어씀.
  const [today, setToday] = useState(readLastKnownToday);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [displaySteps, setDisplaySteps] = useState(null);
  const [busy, setBusy] = useState(false);
  const nativeMode = pedometerAvailable();
  const cumRef = useRef(0);
  const todayRef = useRef(null);
  const lastTplRef = useRef(null);

  // 네이티브: 서버 알림 문구(steps_notif_template)를 받아 네이티브 서비스로 1회 전달(값 변경 시에만).
  // 다음 알림 갱신부터 반영. 웹/미지원은 setNotifTemplate no-op.
  useEffect(() => {
    if (!nativeMode) return;
    const tpl = today?.steps_notif_template;
    if (!tpl || tpl === lastTplRef.current) return;
    lastTplRef.current = tpl;
    try { setNotifTemplate(tpl); } catch (e) { /* noop */ }
  }, [nativeMode, today?.steps_notif_template]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/steps/today");
      setToday(data); todayRef.current = data; setErr(false);
      persistLastKnownToday(data);
    } catch (e) { setErr(true); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { todayRef.current = today; }, [today]);

  // today가 실제값(placeholder 아님)으로 바뀔 때마다 lastKnown 보관 → 다음 진입 즉시 표시
  useEffect(() => { persistLastKnownToday(today); }, [today]);

  // (2-a) 포그라운드 복귀/화면 재진입 시 /steps/today reload — 걸음·claimable·pending 최신화.
  // 네이티브 실시간 폴링(아래 effect)과 별개로 추가. 3초 throttle.
  useEffect(() => {
    let last = 0;
    const onResume = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - last < 3000) return;
      last = now;
      load();
    };
    window.addEventListener("focus", onResume);
    document.addEventListener("visibilitychange", onResume);
    return () => {
      window.removeEventListener("focus", onResume);
      document.removeEventListener("visibilitychange", onResume);
    };
  }, [load]);

  // raw 누적값 → 오늘 표시값 계산(서버 baseline 사용, 음수 방지, 서버 steps와 단조 유지)
  const computeDisplay = (cum) => {
    const t = todayRef.current;
    const serverSteps = t?.steps || 0;
    const base = t?.baseline;
    if (base == null || cum == null) return serverSteps;
    return Math.max(serverSteps, Math.max(0, cum - base));
  };

  // 네이티브: 표시 즉시 갱신(센서 폴링 + 이벤트) + 느린 서버 동기화. 화면 이탈 시 타이머 정리.
  useEffect(() => {
    if (!realtime || !nativeMode) return;
    let mounted = true; let stop = () => {}; let appL = null;
    let firstSynced = false; // 첫 유효 cumulative(양수) 수신 시 25초 주기를 기다리지 않고 1회 즉시 동기화.
    const sync = async () => {
      const c = await getRawCumulative();
      if (c == null) return;
      cumRef.current = c;
      try { const { data } = await api.post("/steps/daily", await buildDailyPayload(c)); if (mounted) { setToday(data); todayRef.current = data; setDisplaySteps(computeDisplay(c)); setTodaySteps(data?.steps, c); } } catch (e) { /* 조용히 재시도 */ }
    };
    // 첫 유효값이면 즉시 서버 동기화(baseline 확립 → 하루 첫 화면이 실제 걸음으로 갱신).
    const maybeFirstSync = (c) => {
      if (!firstSynced && c != null && c > 0) { firstSynced = true; sync(); }
    };
    const bump = async () => { const c = await getRawCumulative(); if (mounted && c != null) { cumRef.current = c; setDisplaySteps(computeDisplay(c)); maybeFirstSync(c); } };
    startRawTracking((c) => { if (mounted) { cumRef.current = c; setDisplaySteps(computeDisplay(c)); maybeFirstSync(c); } }).then((fn) => { stop = fn || (() => {}); });
    bump();
    const dt = setInterval(bump, STEP_DISPLAY_REFRESH_MS);
    const st = setInterval(sync, STEP_SERVER_SYNC_MS);
    import("@capacitor/app").then(({ App }) => {
      App.addListener("appStateChange", ({ isActive }) => { if (!isActive) sync(); else bump(); }).then((h) => { appL = h; });
    }).catch(() => {});
    return () => { mounted = false; clearInterval(dt); clearInterval(st); sync(); stop(); if (appL) { try { appL.remove(); } catch (e) { /* noop */ } } };
  }, [realtime, nativeMode]);

  const perClaim = today?.steps_per_claim || 1000;
  const limit = today?.daily_claim_limit || 0;
  const claimed = today?.claimed_count || 0;
  const hasLive = nativeMode && displaySteps != null;
  // 홈 표시값을 랭킹과 통일: 서버 응답의 max_recognized_steps_per_day 상한을 표시에만 적용.
  // 캡=0(정책 비활성)이면 원본 그대로. 센서 raw·서버 저장·실제 적립(claim 재검증)에는 영향 없음.
  const _rawLive = hasLive ? displaySteps : (today?.steps || 0);
  const _cap = today?.max_recognized_steps_per_day || 0;
  const liveSteps = _cap > 0 ? Math.min(_rawLive, _cap) : _rawLive;
  // UI claimable: 센서값 즉시 반영(버튼 활성용). 실제 적립은 서버가 claim 시 재검증·원자적 가드.
  const claimable = today ? Math.max(0, Math.min(Math.floor(liveSteps / perClaim), limit) - claimed) : 0;

  // 네이티브에서 화면이 표시 중인 라이브 걸음을 전역 발행 → 랭킹 등이 동일 값을 그대로 표시(단일 소스).
  useEffect(() => { if (nativeMode && liveSteps > 0) publishLiveTodaySteps(liveSteps); }, [nativeMode, liveSteps]);

  const claim = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (nativeMode) {
        // 최신 raw 누적값 먼저 서버 반영(baseline 차감·claimable 정확도) → 광고 시청 → claim
        const c = await getRawCumulative();
        if (c != null) { cumRef.current = c; try { const { data } = await api.post("/steps/daily", await buildDailyPayload(c)); setToday(data); todayRef.current = data; } catch (e) { /* noop */ } }
        const rewarded = await showRewardAd();
        if (!rewarded) { toast.message("광고를 끝까지 시청해야 적립돼요"); return; }
      }
      const { data } = await api.post("/steps/claim");
      setToday(data); todayRef.current = data; refresh();
      if (data.ok && data.awarded > 0) toast.success(`${data.awarded}P 적립됐어요`);
      else if (data.claimed_count >= data.daily_claim_limit) toast.message("오늘 적립 다 받았어요");
      else toast.message("아직 적립할 수 없어요");
    } catch (e) {
      toast.error(e.response?.data?.detail || "적립에 실패했어요. 잠시 후 다시 시도해주세요");
    } finally { setBusy(false); }
  }, [busy, nativeMode, refresh]);

  // 연속 보너스 수령(광고 보고 받기) — 네이티브는 AdMob 후, 웹은 바로 streak-claim. pending>0만 가능.
  const [streakBusy, setStreakBusy] = useState(false);
  const claimStreak = useCallback(async () => {
    if (streakBusy) return;
    setStreakBusy(true);
    try {
      if (nativeMode) {
        const rewarded = await showRewardAd();
        if (!rewarded) { toast.message("광고를 끝까지 시청해야 받을 수 있어요"); return; }
      }
      const { data } = await api.post("/steps/streak-claim");
      setToday((prev) => { const next = { ...(prev || {}), ...data }; delete next._placeholder; delete next._reset; delete next._zero; todayRef.current = next; return next; });
      refresh();
      if (data.ok && data.awarded > 0) toast.success(`연속 보너스 +${data.awarded}P 받았어요`);
      else toast.message("받을 수 있는 보너스가 없어요");
    } catch (e) {
      toast.error(e.response?.data?.detail || "수령에 실패했어요. 잠시 후 다시 시도해주세요");
    } finally { setStreakBusy(false); }
  }, [streakBusy, nativeMode, refresh]);

  return { today, loading, err, nativeMode, liveSteps, claimable, busy, claim, streakBusy, claimStreak, reload: load, setToday };
}
